import type { JSX } from 'react';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

import { useImpersonation } from '../../hooks/useImpersonation';

import styles from './ImpersonationBanner.module.css';

/**
 * ImpersonationBanner Component
 *
 * Displays a persistent banner at the top of the page when impersonating
 * a client tenant. Shows the target tenant name and provides a button
 * to return to the Master tenant.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const { isImpersonating, originTenant, targetTenant, endImpersonation } = useImpersonation();

  if (!isImpersonating || !targetTenant) {
    return null;
  }

  const handleExit = async () => {
    try {
      await endImpersonation();
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    }
  };

  return (
    <div className={styles.Banner}>
      <div className={styles.Content}>
        <div className={styles.Warning}>
          <AlertTriangle className={styles.WarningIcon} />
          <span className={styles.Text}>
            <strong>Impersonating:</strong> You are viewing as{' '}
            <span className={styles.TenantName}>{targetTenant.name}</span>
          </span>
        </div>

        <button type="button" className={styles.ExitButton} onClick={handleExit}>
          <ArrowLeft className={styles.ExitIcon} />
          <span>Return to {originTenant?.name || 'Master'}</span>
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;

